import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CaptionsIcon, LanguagesIcon, XIcon } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import ExclusiveBadge from "../ExclusiveBadge";
import PlayerProgressBar from "./components/PlayerProgressBar";
import PlayerControls from "./components/PlayerControls";
import PlayerPreferenceModal from "./components/PlayerPreferenceModal";
import type { PlayerTrack } from "@/types/player.type";
import type { VideoVerticalPlayerSideControls } from "./components/PlayerSideControl";
import { useAppStore } from "@/stores/useApp";
import { cn } from "@/lib/utils";
import useShaka from "@/hooks/useShaka";
import { useVideoPlayerStore } from "@/stores/useVideoPlayer";

interface VideoVerticalPlayerProps extends React.ComponentProps<"div"> {
  url: string;
  allowAutoPlay?: boolean;
  isOnScreen?: boolean;
  videoDetails?: React.ReactNode;
  videoDetailsSkeleton?: React.ReactNode;
  onEndReached?: () => void;
  onProgressChange?: (progress: number) => void;
  onClose?: () => void;
  onAutoPlay?: () => void;
  onSubtitleClick?: () => void;
  onSubtitleChange?: (subtitle: PlayerTrack) => void;
  onAudioClick?: () => void;
  onAudioChange?: (audio: PlayerTrack) => void;
  /** The mute button, with the state it set. */
  onMuteChange?: (muted: boolean) => void;
  sideControls?: Array<VideoVerticalPlayerSideControls>;
}

type VideoVerticalPlayerType = React.FC<VideoVerticalPlayerProps> & {
  Skeleton: React.FC<VideoVerticalPlayerSkeletonProps>;
};

interface VideoVerticalPlayerSkeletonProps extends React.HTMLAttributes<HTMLElement> {
  videoDetails?: React.ReactNode;
  sideControls?: Array<VideoVerticalPlayerSideControls>;
  onClose?: () => void;
}

const VideoVerticalPlayerSkeleton: React.FC<
  VideoVerticalPlayerSkeletonProps
> = ({ sideControls, videoDetails, onClose, ...props }) => (
  <div
    {...props}
    className={cn("relative flex h-full w-full", props.className)}
  >
    {onClose && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className={cn(
          "pointer-events-auto absolute top-0 left-0 z-50 h-12 w-16 transition-opacity duration-300",
        )}
      >
        <XIcon className="mx-auto size-8 text-white" />
      </button>
    )}
    <PlayerControls.Skeleton
      sideControls={sideControls ?? []}
      className="lg:hidden"
    >
      {videoDetails}
    </PlayerControls.Skeleton>
    <ExclusiveBadge className="absolute top-1/2 right-1/2 z-50 size-24 translate-x-1/2 -translate-y-1/2 animate-pulse text-neutral-50" />
  </div>
);

const VideoVerticalPlayer: VideoVerticalPlayerType = ({
  url,
  allowAutoPlay = true,
  isOnScreen = false,
  sideControls,
  videoDetails,
  videoDetailsSkeleton,
  onEndReached,
  onAutoPlay,
  onProgressChange,
  onSubtitleClick,
  onSubtitleChange,
  onAudioClick,
  onAudioChange,
  onMuteChange,
  onClose,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const soundMuted = useAppStore((s) => s.soundMuted);
  const { setAudioPreference, setSubtitlesPreference, disableSubtitles } =
    useAppStore(
      useShallow((s) => ({
        setAudioPreference: s.setAudioPreference,
        setSubtitlesPreference: s.setSubtitlesPreference,
        disableSubtitles: s.disableSubtitles,
      })),
    );

  const { setVideoPlayer, setHlsContext, setSubtitles, setAudioTracks } =
    useVideoPlayerStore(
      useShallow((s) => ({
        setVideoPlayer: s.setVideoPlayer,
        setHlsContext: s.setHlsContext,
        setSubtitles: s.setSubtitles,
        setAudioTracks: s.setAudioTracks,
      })),
    );

  const videoRef = useRef<HTMLVideoElement>(null);

  const [openSubtitleModal, setOpenSubtitleModal] = useState(false);
  const [openAudioModal, setOpenAudioModal] = useState(false);

  const { progress, isReady, isPlaying, hlsContext } = useShaka({
    url,
    videoElementRef: videoRef,
    onEndReached,
    onProgressChange,
  });

  const subtitles = hlsContext?.allSubtitleTracks ?? [];
  const audioTracks = hlsContext?.allAudioTracks ?? [];

  const onAutoPlayRef = useRef(onAutoPlay);
  onAutoPlayRef.current = onAutoPlay;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!allowAutoPlay) {
      if (!v.paused) v.pause();
      // Rewind once the swipe has settled, so coming back to this episode
      // doesn't flash the frame we paused on. Deferred (not done synchronously
      // here) to avoid racing the incoming slide's play() — iOS Safari rejects
      // with NotAllowedError when a concurrent seek happens mid-swipe.
      const rewindTimeout = setTimeout(() => {
        if (v.currentTime !== 0) v.currentTime = 0;
      }, 500);
      return () => clearTimeout(rewindTimeout);
    }
    if (!isReady) return;
    // v.paused (not React state) — the isPlaying state can lag behind the
    // pause event when the intersection observer flickers across a swipe,
    // which would skip the play() and leave the slide stuck paused.
    if (!v.paused) return;
    // Reset on the slide that's *becoming* active — avoids seeking the
    // outgoing video while the new one calls play(), which iOS Safari
    // occasionally rejects with NotAllowedError mid-swipe.
    if (v.currentTime !== 0) v.currentTime = 0;
    onAutoPlayRef.current?.();
    v.play().catch((error: DOMException) => {
      if (error.name === "NotAllowedError") {
        // iOS blocks autoplay with sound when play() is detached from the swipe
        // gesture. We mute to keep playback, and mirror it in the store so the
        // controlled `muted` prop stays in sync (React only rewrites el.muted on
        // a value change) — otherwise the element stays muted while the UI shows
        // sound enabled. The unmute tap is a real gesture, so it restores audio.
        v.muted = true;
        useAppStore.setState({ soundMuted: true });
        v.play().catch(() => {});
        return;
      }
      console.warn("Autoplay was prevented", error);
    });
  }, [allowAutoPlay, isReady]);

  useEffect(() => {
    if (!isOnScreen) return;
    if (videoRef.current) setVideoPlayer(videoRef.current);
    if (hlsContext) {
      setHlsContext(hlsContext);
      setSubtitles(subtitles);
      setAudioTracks(audioTracks);
    }
    // hlsContext: it lands only once `load()` resolves, which can be after
    // `canplay` already flipped isReady — without it in the deps that ordering
    // leaves the desktop controls with no tracks for the whole session.
    // subtitles/audioTracks are getters returning a fresh array on every
    // render, so they can't be listed; they are fully derived from hlsContext
    // and resolvedLanguage, which are.
    // resolvedLanguage: the track names are snapshots, so a language switch has
    // to re-push them or the desktop controls keep the previous locale's labels.
  }, [isOnScreen, videoRef, isReady, i18n.resolvedLanguage, hlsContext]);

  const sideControlsExtended = useMemo(
    () => [
      ...(sideControls ?? []),
      ...(subtitles.length > 0
        ? [
            {
              label: t("player_subtitles_control_label"),
              icon: CaptionsIcon,
              onClick: () => {
                setOpenSubtitleModal(true);
                onSubtitleClick?.();
              },
            },
          ]
        : []),
      ...(audioTracks.length > 1
        ? [
            {
              label: t("player_audio_control_label"),
              icon: LanguagesIcon,
              onClick: () => {
                setOpenAudioModal(true);
                onAudioClick?.();
              },
            },
          ]
        : []),
    ],
    [sideControls, subtitles.length, audioTracks.length, t],
  );

  const onPlayPause = () => {
    if (!videoRef.current) return;

    // A pause() landing before play() settles (swipe, slide leaving the
    // viewport) rejects it with AbortError — the video is paused, as asked.
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play().catch(() => {});
  };

  return (
    <>
      <div
        {...props}
        className={cn(
          "relative flex h-full w-full overflow-y-auto",
          props.className,
        )}
      >
        {!isReady && (
          <VideoVerticalPlayerSkeleton
            className="bg-neutral-70 absolute z-50 h-full"
            sideControls={sideControlsExtended}
            videoDetails={videoDetailsSkeleton}
            onClose={onClose}
          />
        )}
        {url && isReady && (
          <PlayerControls
            isActive={isReady}
            isPlaying={isPlaying}
            onPlayPause={onPlayPause}
            onMuteChange={onMuteChange}
            sideControls={sideControlsExtended}
            onClose={onClose}
          >
            {videoDetails}
            <PlayerProgressBar progress={progress} />
          </PlayerControls>
        )}

        <video
          ref={videoRef}
          className="-z-10 h-full w-full object-contain"
          playsInline
          muted={soundMuted}
        />
      </div>

      {subtitles.length > 0 && (
        <PlayerPreferenceModal
          title={t("player_subtitles_menu_title_web")}
          open={openSubtitleModal}
          mediaList={[
            { id: -1, name: t("player_off_label_web"), language: "" },
            ...subtitles,
          ]}
          activePlayerPreferenceIndex={hlsContext?.subtitleTrack ?? null}
          onOpenChange={setOpenSubtitleModal}
          onPlayerPreferenceChange={(subtitleIndex) => {
            if (hlsContext) {
              // -1 is the "Off" row prepended above, not an index into
              // `subtitles` — it has no track to read a language from.
              const subtitle =
                subtitleIndex === -1 ? null : subtitles[subtitleIndex];
              hlsContext.subtitleTrack = subtitleIndex;
              if (subtitle) {
                setSubtitlesPreference(subtitle.language);
                onSubtitleChange?.(subtitle);
              } else {
                disableSubtitles();
              }
            }
          }}
        />
      )}
      {audioTracks.length > 1 && (
        <PlayerPreferenceModal
          title={t("player_audio_menu_title_web")}
          open={openAudioModal}
          mediaList={audioTracks}
          activePlayerPreferenceIndex={hlsContext?.audioTrack ?? null}
          onOpenChange={setOpenAudioModal}
          onPlayerPreferenceChange={(audioIndex) => {
            if (hlsContext) {
              hlsContext.audioTrack = audioIndex;
              setAudioPreference(audioTracks[audioIndex].language);
              onAudioChange?.(audioTracks[audioIndex]);
            }
          }}
        />
      )}
    </>
  );
};

VideoVerticalPlayer.Skeleton = VideoVerticalPlayerSkeleton;

export default VideoVerticalPlayer;
